import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import JSONPretty from 'react-json-pretty';
import { useSearchParams } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchCpoConnectionLogFilters,
  fetchCpoConnectionLogs,
  setSortData,
  updateCPOConnectionLogFilters,
} from '../../../../redux/slices/OCPI/CPOConnectionLogsSlice';
import { RootState } from '../../../../redux/store';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import TabToggle from '../../../../shared-components/tab-toggle/TabToggle';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import ocpiApi from '../../../../utils/api/ocpi';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiCommand {
  connectionId: string;
  direction: string;
  createdAt: string;
  domain: string;
  ipAddress: string;
  masterTenantId: string;
  method: string;
  module: string;
  path: string;
  query: any;
  requestHeaders: any;
  requestBody?: unknown;
  requestPayload?: unknown;
  error?: unknown;
  errorDetails?: unknown;
  responseBody: string;
  responseStatus: string;
  responseTime: string;
  updatedAt: string;
  url: string;
  ocpi: string;
  _id: string;
}

interface ConnectionLogRow {
  id: string;
  direction: string;
  module: string;
  method: string;
  query: any;
  requestHeaders: any;
  requestBody?: unknown;
  requestPayload?: unknown;
  error?: unknown;
  errorDetails?: unknown;
  responseBody: string;
  responseStatus: string;
  responseTime: string;
  url: string;
  ocpi: string;
  createdAt: string;
}

type LogDetailTab = 'payload' | 'error' | 'header';

/**
 * Returns the first populated value from the possible API field names.
 * @param {OcpiCommand} command The selected connection log.
 * @param {string[]} keys Candidate field names.
 * @returns {unknown} The first populated field value.
 */
const getLogValue = (command: OcpiCommand, keys: string[]): unknown => {
  const logRecord = command as unknown as Record<string, unknown>;
  return (
    keys.map((key) => logRecord[key]).find((value) => value !== undefined && value !== null) ?? null
  );
};

/**
 * Converts a log value to readable text without failing on plain strings.
 * @param {unknown} value The value to format.
 * @returns {string} Formatted value text.
 */
const formatLogValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Converts request headers into key/value rows for the header list view.
 * @param {unknown} headers The request headers value.
 * @returns {Array<[string, string]>} Header name and value pairs.
 */
const getHeaderEntries = (headers: unknown): Array<[string, string]> => {
  if (!headers) return [];
  let parsedHeaders = headers;

  if (typeof headers === 'string') {
    try {
      parsedHeaders = JSON.parse(headers);
    } catch {
      return [['Header', headers]];
    }
  }

  if (typeof parsedHeaders !== 'object' || parsedHeaders === null) {
    return [['Header', String(parsedHeaders)]];
  }

  return Object.entries(parsedHeaders as Record<string, unknown>).map(([key, value]) => [
    key,
    typeof value === 'string' ? value : formatLogValue(value),
  ]);
};

/**
 * Displays the selected OCPI connection log in a tabbed side panel.
 * @returns {JSX.Element} The log detail panel.
 */
function CpoConnectionLogSidePanel(): JSX.Element {
  const { t } = useTranslation(['ocpi']);
  const { connectionLogs } = useAppSelector((state: RootState) => state.cpoConnectionLog);
  const [searchParameters] = useSearchParams();
  const selectedId = searchParameters.get('id');
  const selectedLog = connectionLogs.find((log: OcpiCommand) => log._id === selectedId);
  const [activeTab, setActiveTab] = useState<LogDetailTab>('payload');

  const requestPayload = selectedLog
    ? getLogValue(selectedLog, ['requestPayload', 'requestBody', 'payload', 'request'])
    : null;
  const errorDetails = selectedLog
    ? getLogValue(selectedLog, ['errorDetails', 'error', 'errorMessage', 'errorBody'])
    : null;
  const headerDetails = selectedLog ? getLogValue(selectedLog, ['requestHeaders']) : null;
  const headerEntries = getHeaderEntries(headerDetails);

  return (
    <div className="cpo-connection-log-side-panel">
      <div className="cpo-connection-log-summary">
        <strong>{selectedLog?.method || '-'}</strong>
        <strong className="cpo-connection-log-summary-url">{selectedLog?.url || '-'}</strong>
        <span>{selectedLog?.responseStatus || '-'}</span>
      </div>
      <TabToggle
        tabs={[
          { label: t('ocpi:payload', { defaultValue: 'Payload' }), value: 'payload' },
          { label: t('ocpi:header', { defaultValue: 'Header' }), value: 'header' },
          { label: t('ocpi:errorDetails', { defaultValue: 'Error details' }), value: 'error' },
        ]}
        activeTab={activeTab}
        isLabelValue
        onChange={(value) => setActiveTab(value as LogDetailTab)}
      />
      <div className="cpo-connection-log-detail-content">
        {activeTab === 'payload' && (
          <>
            <section className="cpo-connection-log-payload-section">
              <h4>{t('ocpi:requestPayload', { defaultValue: 'Request payload' })}</h4>
              <JSONPretty data={requestPayload ?? {}} />
            </section>
            <section className="cpo-connection-log-payload-section">
              <h4>{t('ocpi:responsePayload', { defaultValue: 'Response payload' })}</h4>
              <JSONPretty data={selectedLog?.responseBody || {}} />
            </section>
          </>
        )}
        {activeTab === 'header' && (
          <section className="cpo-connection-log-payload-section">
            <h4>{t('ocpi:header', { defaultValue: 'Headers' })}</h4>
            <div className="cpo-connection-log-header-list">
              {headerEntries.length > 0 ? (
                headerEntries.map(([key, value]) => (
                  <div className="cpo-connection-log-header-row" key={key}>
                    <strong>{key}</strong>
                    <span>{value}</span>
                  </div>
                ))
              ) : (
                <span>-</span>
              )}
            </div>
          </section>
        )}
        {activeTab === 'error' && <pre>{formatLogValue(errorDetails)}</pre>}
      </div>
    </div>
  );
}

/**
 * CpoCommandsPanel component displays OCPI commands received from connected eMSPs.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 * @param {any} props.filterCountryCode Optional country code of the eMSP to filter by.
 * @param {any} props.filterPartyId Optional party ID of the eMSP to filter by.
 * @param {any} props.refreshTrigger Optional number that triggers a reload when incremented.
 */
export default function CpoConnectionLogsPanel({
  connectionId,
}: Readonly<{
  connectionId: string;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const [, setSearchParameters] = useSearchParams();
  const {
    connectionLogs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.cpoConnectionLog);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  /**
   * Function to clear the date range.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };
  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches CPO commands for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      const request: any = {
        role: 'CPO',
        connectionId,
        ...data,
      };
      if (dateRange.startDate) request.startDate = dateRange.startDate;
      if (dateRange.endDate) request.endDate = dateRange.endDate;
      dispatch(fetchCpoConnectionLogs(request));
    }
  };

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof ConnectionLogRow,
      sortAllow: true,
      /**
       * Rendering created date
       * @param {ConnectionLogRow} currentRow The current command row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: ConnectionLogRow) => (
        <span>{ConvertUTCtoDateTime(currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:direction'),
      accessorKey: 'direction' as keyof ConnectionLogRow,
      sortAllow: true,
      columnWidth: 180,
      /**
       * Renders the command ID in a code badge.
       * @param {any} row The command row.
       */
      customData: (row: ConnectionLogRow) => <code>{row.direction}</code>,
    },
    {
      label: t('ocpi:module'),
      accessorKey: 'module' as keyof ConnectionLogRow,
      sortAllow: true,
      columnWidth: 180,
      /**
       * Renders the command ID in a code badge.
       * @param {any} row The command row.
       */
      customData: (row: ConnectionLogRow) => <code>{row.module}</code>,
    },
    {
      label: t('ocpi:method'),
      accessorKey: 'method' as keyof ConnectionLogRow,
      sortAllow: true,
      /**
       * Renders the source eMSP badge.
       * @param {any} row The command row.
       */
      customData: (row: ConnectionLogRow) => <span>{row.method}</span>,
    },
    {
      label: t('ocpi:http'),
      accessorKey: 'responseStatus' as keyof ConnectionLogRow,
      sortAllow: true,
      /**
       * Renders the source eMSP badge.
       * @param {any} row The command row.
       */
      customData: (row: ConnectionLogRow) => <span>{row.responseStatus}</span>,
    },
    {
      label: t('ocpi:responseTime'),
      accessorKey: 'responseTime' as keyof ConnectionLogRow,
      sortAllow: true,
      /**
       * Renders the status badge.
       * @param {any} row The command row.
       */
      customData: (row: ConnectionLogRow) => <div>{row.responseTime} ms</div>,
    },
    {
      label: t('ocpi:ocpi'),
      accessorKey: 'ocpi' as keyof ConnectionLogRow,
      sortAllow: true,
      /**
       * Renders the status badge.
       * @param {any} row The command row.
       */
      customData: (row: ConnectionLogRow) => <div>{row.ocpi || t('na')}</div>,
    },
    {
      label: t('ocpi:status'),
      accessorKey: 'status' as keyof ConnectionLogRow,
      /**
       * Renders the status badge.
       * @param {any} row The command row.
       */
      customData: (row: ConnectionLogRow) => (
        <div>{[200, 201].includes(Number(row.responseStatus)) ? 'Success' : 'Error'}</div>
      ),
    },
  ];

  const tableData: ConnectionLogRow[] = connectionLogs.map((command: OcpiCommand) => ({
    id: command._id,
    direction: command.direction,
    module: command.module,
    method: command.method,
    query: command.query,
    requestHeaders: command.requestHeaders,
    responseBody: command.responseBody,
    responseStatus: command.responseStatus,
    responseTime: command.responseTime,
    ocpi: command.ocpi,
    url: command.url,
    createdAt: command.createdAt,
    requestBody: command.requestBody,
    requestPayload: command.requestPayload,
    error: command.error,
    errorDetails: command.errorDetails,
  }));

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField: sortField ?? undefined, sortBy: sortBy ?? undefined });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('ocpi:created'), accessorKey: 'createdAt' },
    { label: t('ocpi:direction'), accessorKey: 'direction' },
    { label: t('ocpi:module'), accessorKey: 'module' },
    { label: t('ocpi:method'), accessorKey: 'method' },
    { label: t('ocpi:http'), accessorKey: 'responseStatus' },
    { label: t('ocpi:responseTime'), accessorKey: 'responseTime' },
    { label: t('ocpi:ocpi'), accessorKey: 'ocpi' },
    { label: t('ocpi:status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter((col) => {
    return col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
      role: 'CPO',
      connectionId,
    };
    if (dateRange.startDate) payload.startDate = dateRange.startDate;
    if (dateRange.endDate) payload.endDate = dateRange.endDate;
    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;
    ocpiApi
      .getInternalCpoConnectionLogExport(payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.results.map((row: ConnectionLogRow, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt) || t('na');
              case 'responseTime':
                return `${row.responseTime} ms`;
              case 'status':
                return [200, 201].includes(Number(row.responseStatus)) ? 'Success' : 'Error';
              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'ocpi-logs-list.csv');
        } else {
          exportPDF(headers, rows, 'ocpi-logs-list.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the table's sort change.
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Opens the clicked log in CustomTable's right-side panel.
   * @param {string} id The selected connection log identifier.
   * @returns {void}
   */
  const handleLogRowClick = (id: string): void => {
    setSearchParameters((previous) => {
      const updatedParameters = new URLSearchParams(previous.toString());
      updatedParameters.set('id', id);
      return updatedParameters;
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  }, [filters, dateRange]);

  return (
    <div className="cpo-commands-panel">
      {/* ── Top bar ── */}
      <div className="cpo-commands-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:logs')}</h3>
          <p className="cpo-panel-subtitle">
            {connectionLogs.length > 0
              ? `${connectionLogs.length} ${t(
                  connectionLogs.length !== 1
                    ? 'ocpi:commandCountPlural'
                    : 'ocpi:commandCountSingular',
                )} received from connected eMSPs`
              : t('ocpi:noCommandsReceived')}
          </p>
        </div>
      </div>

      {/* ── Commands Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateCPOConnectionLogFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchCpoConnectionLogFilters}
        exportData={tableData}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={
          permissionCheck('mobilityEndConsumerExport') ? exportDataPdfOrCSV : undefined
        }
        rightTopContent={<div className="table-bottom-button-list">{renderDateFilters()}</div>}
        onClearFilterChange={() => {
          clearDateRange();
        }}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={handleLogRowClick}
        rowClickParamsKey="id"
        rowClickSidePanel={<CpoConnectionLogSidePanel />}
        onHiddenColumnsChange={setHiddenColumns}
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
      />
    </div>
  );
}
